/* eslint eqeqeq: 0 */

import { useContext,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useRequest } from '@main/hooks';
import { Autocomplete, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import { stringToKebabCase } from 'app/utils/transformers';
import { createFilterOptions } from '@mui/material/Autocomplete';
import _ from 'lodash';

import { useDispatch } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';

const filter = createFilterOptions();

const DEFAULT_VALUE_KEY = 'id';
const DEFAULT_LABEL_KEY = 'name';

/**
 * AutocompleteSelector - Componente de selección con autocompletado y request manejado internamente.
 * [NOTA]: En caso de no activar `showAllOption` el valor inicial debe ser `null`.
 *
 * @component
 * @param {Function} request - Referencia de la función para realizar la request de datos.
 * @param {any} value - Valor seleccionado en el autocompletado.
 * @param {Function} onChange - Función de manejo de cambios en la selección.
 * @param {string} title - Etiqueta para el TextField.
 * @param {boolean} [autoFetch=true] - Indica si se debe realizar la solicitud automáticamente.
 * @param {boolean} [showAllOption=false] - Define si se debe incluir la opción "Todo".
 * @param {boolean} [concatOptionLabel=false] - Define si se debe concatenar la etiqueta mostrada.
 * @param {string} [valueKey="id"] - Clave de valor en los datos obtenidos.
 * @param {string} [labelKey="name"] - Clave de etiqueta en los datos obtenidos.
 * @param {Object} [inputProps={}] - Propiedades adicionales para el TextField.
 * @param {Object} props - Propiedades adicionales que se pueden pasar al Autocomplete.
 *
 * @returns {JSX.Element} Componente Autocomplete.
 *
 * @example
 * <AutocompleteSelector
 *   request={fetchData}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   inputProps={{
 *      label="Seleccionar opción"
 *   }}
 * />
 */
export const AutocompleteSelector = forwardRef(
  (
    {
      className,
      request,
      value,
      onChange,
      onLoad,
      title,
      isInvisible,
      inputProps = {},
      autoFetch = true,
      showAllOption = false,
      concatOptionLabel = false,
      enableCreateOption = false,
      onlyStringsMode = true,
      labelKey = DEFAULT_LABEL_KEY,
      valueKey = DEFAULT_VALUE_KEY,
      ...props
    },
    ref
  ) => {
    if (typeof request !== 'function')
      throw new Error(`'request' prop must be a valid function ref`);

    const allOptionStruct = useMemo(
      () => ({
        [valueKey]: '',
        [labelKey]: 'Todo',
      }),
      [valueKey, labelKey]
    );

    // ------ Initialization
    const dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
    const [data, setData] = useState([]);

    const defaultValue = useMemo(() => {
      return props.multiple ? [] : showAllOption ? '' : null;
    }, [props.multiple, showAllOption]);

    const handleOnForbidden = async () => {
    await finalizeSession();
  };

    const { makeRequest, response } = useRequest(request, {
      onForbidden: handleOnForbidden,
    });

    useImperativeHandle(ref, () => {
      return {
        makeRequest,
      };
    });

    useLayoutEffect(() => {
      if (autoFetch === false || autoFetch === null || autoFetch === undefined) return;
      makeRequest();
    }, [autoFetch]);

    useEffect(() => {
      // -- Si no hay respuesta o es igual a los datos actuales, no hacer nada
      if (!response || _.isEqual(response, data)) return;
      console.log({ autocomplete: response });
      const validResponse = Array.isArray(response) ? response : [];
      setData(showAllOption ? [allOptionStruct, ...validResponse] : validResponse);
    }, [response, showAllOption, allOptionStruct]);

    useEffect(() => {
      if (!response) return;
      if (onLoad) onLoad(response);
    }, [response]);

    const handleIsOptionEqualToValue = (option, currentVal) => {
      if (typeof option === 'object' && typeof currentVal === 'object')
        return option[valueKey] == currentVal[valueKey];
      if (typeof option === 'object') return option[valueKey] == currentVal;
      return option == currentVal;
    };

    const handleGetOptionLabel = (option) => {
      if (typeof option !== 'object') return option;
      if (!concatOptionLabel) {
        return option[labelKey] ?? '';
      }
      return option[valueKey] === ''
        ? option[labelKey]
        : `${option[valueKey]} - ${option[labelKey]}`;
    };

    const handleGetOptionKey = (option) => {
      if (typeof option !== 'object') return option;
      return `${option[valueKey]}-${stringToKebabCase(option[labelKey] || '')}`;
    };

    const handleFilterOptions = (options, params) => {
      const filtered = filter(options, params);
      const { inputValue } = params;
      const isExisting = options.some((option) => inputValue === option[labelKey]);
      if (inputValue !== '' && !isExisting) {
        filtered.push(
          onlyStringsMode
            ? inputValue
            : {
                [valueKey]: inputValue,
                [labelKey]: inputValue,
              }
        );
      }
      return filtered;
    };

    return (
      <Autocomplete
        {...props}
        ref={ref}
        className={clsx(className, isInvisible && 'hidden')}
        options={data}
        value={value || defaultValue}
        onChange={(e, val) => onChange(val || defaultValue)}
        isOptionEqualToValue={handleIsOptionEqualToValue}
        getOptionLabel={handleGetOptionLabel}
        getOptionKey={handleGetOptionKey}
        filterOptions={enableCreateOption ? handleFilterOptions : undefined}
        renderOption={(rest, option, { selected }) => {
          const { key, ...optionProps } = rest;
          return (
            <li key={key} {...optionProps}>
              <Typography className={selected ? 'font-bold' : ''}>
                {handleGetOptionLabel(option)}
              </Typography>
            </li>
          );
        }}
        renderInput={(params) => <TextField {...params} {...inputProps} />}
      />
    );
  }
);
